import { useMemo, memo } from 'react';
import {
  Table,
  TypographyWithTooltip,
  useTranslations,
  IUseTableReturn,
  IUseTableFetchReturn,
  IUseTableLocalizationReturn,
  IMaterialTableOptions,
  formatDate,
  DateTimeFormatEnum,
  getMonthName,
} from '@uniqkey-frontend/shared-app';
import {
  GetOrganizationHistoricLicenseDataResponseModel,
} from '@uniqkey-backend-partner/api-client';

export interface IOrganizationLicensesHistoryTableRow extends
  GetOrganizationHistoricLicenseDataResponseModel {
  processedDate: string | null;
  month: string | null;
}

interface IOrganizationLicensesHistoryTableProps extends
  Omit<IUseTableReturn<IOrganizationLicensesHistoryTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Omit<IUseTableFetchReturn<GetOrganizationHistoricLicenseDataResponseModel>, 'data' | 'refetch'>
{
  organizationLicensesHistory: IUseTableFetchReturn<
    GetOrganizationHistoricLicenseDataResponseModel
  >['data'];
  selectedOrganizationLicensesHistory: IUseTableReturn<
    IOrganizationLicensesHistoryTableRow
  >['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
}

const TABLE_OPTIONS: IMaterialTableOptions<IOrganizationLicensesHistoryTableRow> = {
  selection: false,
};

const OrganizationLicensesHistoryTable = (props: IOrganizationLicensesHistoryTableProps) => {
  const {
    organizationLicensesHistory: preOrganizationLicensesHistory,
    isLoading,
    total,
    localization,
    activePage,
    setActivePage,
    perPage,
    onPerPageChange,
    columnOrderBy,
    onColumnOrderByChange,
    columnOrderDirection,
    onColumnOrderDirectionChange,
    selectedOrganizationLicensesHistory,
    onRowSelectionChange,
    createColumns,
  } = props;

  const { t, currentLanguage } = useTranslations();

  const organizationLicensesHistory = useMemo(() => preOrganizationLicensesHistory.map(
    (licenseHistory) => ({
      ...licenseHistory,
      processedDate: formatDate({ date: licenseHistory.date }, DateTimeFormatEnum.DEFAULT),
      month: getMonthName({
        date: licenseHistory.date,
        locale: currentLanguage,
      }),
    }),
  ), [preOrganizationLicensesHistory, currentLanguage]);

  const columns = useMemo(() => createColumns([
    {
      title: t('organizationPage.licensesHistoryTab.date'),
      field: 'date',
      render: (rowData) => (
        <TypographyWithTooltip variant="body1">
          {rowData.processedDate}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
    {
      title: t('organizationPage.licensesHistoryTab.licenses'),
      field: 'licenseCount',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {rowData.licenseCount}
        </TypographyWithTooltip>
      ),
      width: '15%',
    },
    {
      title: t('organizationPage.licensesHistoryTab.description'),
      field: 'description',
      render: (rowData) => (
        <TypographyWithTooltip variant="body2">
          {t('organizationPage.licensesHistoryTab.licenseDescription', { month: rowData.month })}
        </TypographyWithTooltip>
      ),
      width: '70%',
      sorting: false,
    },
  ]), [createColumns, t]);

  return (
    <Table
      t={t}
      totalItems={total}
      columns={columns}
      data={organizationLicensesHistory}
      isLoading={isLoading}
      options={TABLE_OPTIONS}
      activePage={activePage}
      onActivePageChange={setActivePage}
      perPage={perPage}
      onPerPageChange={onPerPageChange}
      columnOrderBy={columnOrderBy}
      onColumnOrderByChange={onColumnOrderByChange}
      columnOrderDirection={columnOrderDirection}
      onColumnOrderDirectionChange={onColumnOrderDirectionChange}
      selectedRows={selectedOrganizationLicensesHistory}
      onSelectionChange={onRowSelectionChange}
      localization={localization}
      rowSelectByKey="date"
      rowHeight={51}
    />
  );
};

export default memo(OrganizationLicensesHistoryTable);
